import { useState } from 'react';
import { Form, Input, Button, Card, Typography, Space, App, Alert } from 'antd';
import { UserOutlined, LockOutlined, DatabaseOutlined } from '@ant-design/icons';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ThemeToggle } from '../components/common/ThemeToggle';
import { changePassword } from '../api/settings';

const { Title, Text } = Typography;

interface LoginFormValues {
  username: string;
  password: string;
}

interface PasswordChangeValues {
  new_password: string;
  confirm_password: string;
}

export function LoginPage() {
  const { login, isAuthenticated, isLoading, mustChangePassword, clearMustChangePassword } = useAuth();
  const { message } = App.useApp();
  const [form] = Form.useForm<LoginFormValues>();
  const [passwordForm] = Form.useForm<PasswordChangeValues>();
  const [loginCredentials, setLoginCredentials] = useState<LoginFormValues | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);

  if (isAuthenticated && !mustChangePassword) {
    return <Navigate to="/products" replace />;
  }

  const handleSubmit = async (values: LoginFormValues) => {
    try {
      setLoginCredentials(values);
      await login({ username: values.username, password: values.password });
      // If mustChangePassword becomes true, the form below will show
    } catch (error) {
      message.error('Invalid username or password');
    }
  };

  const handlePasswordChange = async (values: PasswordChangeValues) => {
    if (!loginCredentials) return;

    setChangingPassword(true);
    try {
      await changePassword({
        current_password: loginCredentials.password,
        new_password: values.new_password,
      });
      message.success('Password changed successfully');
      clearMustChangePassword();
    } catch (error) {
      message.error('Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Show password change form after login if required
  if (isAuthenticated && mustChangePassword) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: 24,
        }}
      >
        <Card
          style={{
            width: '100%',
            maxWidth: 400,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Space
            direction="vertical"
            size="large"
            style={{ width: '100%', textAlign: 'center' }}
          >
            <div>
              <DatabaseOutlined
                style={{ fontSize: 48, color: '#1677ff', marginBottom: 16 }}
              />
              <Title level={3} style={{ margin: 0 }}>
                Set Your Password
              </Title>
            </div>

            <Alert
              type="warning"
              message="Please set a secure password to continue."
              showIcon
            />

            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handlePasswordChange}
              style={{ textAlign: 'left' }}
            >
              <Form.Item
                name="new_password"
                rules={[
                  { required: true, message: 'Please enter a new password' },
                  { min: 8, message: 'Password must be at least 8 characters' },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="New Password"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="confirm_password"
                dependencies={['new_password']}
                rules={[
                  { required: true, message: 'Please confirm your password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('new_password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Confirm Password"
                  size="large"
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={changingPassword}
                >
                  Set Password & Continue
                </Button>
              </Form.Item>
            </Form>
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 24,
      }}
    >
      <div style={{ position: 'absolute', top: 24, right: 24 }}>
        <ThemeToggle />
      </div>

      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Space
          direction="vertical"
          size="large"
          style={{ width: '100%', textAlign: 'center' }}
        >
          <div>
            <DatabaseOutlined
              style={{ fontSize: 48, color: '#1677ff', marginBottom: 16 }}
            />
            <Title level={2} style={{ margin: 0 }}>
              PIM
            </Title>
            <Text type="secondary">Product Information Manager</Text>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
            style={{ textAlign: 'left' }}
          >
            <Form.Item
              name="username"
              rules={[{ required: true, message: 'Please enter your username' }]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Username"
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Please enter your password' }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Password"
                size="large"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={isLoading}
              >
                Sign In
              </Button>
            </Form.Item>
          </Form>

        </Space>
      </Card>
    </div>
  );
}
